import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  useLocation,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import { getSiteSettingsPublic } from "@/lib/api/public.functions";
import { defaultFirmContact } from "@/lib/firm-contact";
import { defaultSiteNavSettings } from "@/lib/navigation/defaults";
import { articles, clientStories, serviceCategories } from "@/lib/content";
import type { ServiceCategory } from "@/lib/content";
import { loadServiceCategories, loadInsights, loadStories, loadLeaders, loadCareers } from "@/lib/content-loader";
import { slimInsightsForShell, slimLeadersForShell, slimStoriesForShell } from "@/lib/content-slim";
import type { Leader } from "@/lib/types/content";
import { SearchIndexProvider } from "@/components/site/SearchIndexContext";
import type { SearchIndexInput } from "@/components/site/SearchIndex";
import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { SiteMotionProvider } from "@/components/site/SiteMotionContext";
import { FloatingActions } from "@/components/site/FloatingActions";
import { StickyHeroSearch } from "@/components/site/StickyHeroSearch";
import { Toaster } from "sonner";
import { useTrackPageView } from "@/components/site/useTrackPageView";
import { DeferredAnalytics } from "@/components/site/DeferredAnalytics";
import { buildPublicIndexHead, buildOpenGraphMeta, buildTwitterMeta, DEFAULT_SITE_TITLE, DEFAULT_SITE_DESCRIPTION } from "@/lib/seo/meta";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-gold">404</p>
        <h1 className="mt-3 text-3xl font-bold tracking-tight text-primary">Page not found</h1>
        <p className="mt-3 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90"
          >
            Return home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-2xl font-bold tracking-tight text-primary">This page didn't load</h1>
        <p className="mt-3 text-sm text-muted-foreground">
          Something went wrong on our end. You can try again or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-full border border-input bg-background px-5 py-2.5 text-sm font-semibold text-foreground hover:bg-accent-soft"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  loader: async () => {
    try {
      const [settings, services, insights, stories, leaders, careers] = await Promise.all([
        getSiteSettingsPublic(),
        loadServiceCategories(),
        loadInsights(),
        loadStories(),
        loadLeaders(),
        loadCareers(),
      ]);
      const shellInsights = slimInsightsForShell(insights) as typeof articles;
      const shellStories = slimStoriesForShell(stories) as typeof clientStories;
      const shellLeaders = slimLeadersForShell(leaders) as Leader[];
      const shellServices = services as ServiceCategory[];
      return {
        ...settings,
        megaMenu: { services: shellServices, insights: shellInsights, stories: shellStories },
        searchData: {
          services: shellServices,
          insights: shellInsights,
          stories: shellStories,
          leaders: shellLeaders,
          careers,
          firmContact: settings.firmContact,
        },
      };
    } catch {
      return {
        googleAnalyticsId: "",
        siteUrl: "",
        firmContact: defaultFirmContact,
        nav: defaultSiteNavSettings,
        craftGlobalCss: "",
        cardAnimationsEnabled: true,
        megaMenu: {
          services: serviceCategories,
          insights: articles,
          stories: clientStories,
        },
        searchData: {
          services: serviceCategories,
          insights: articles,
          stories: clientStories,
          leaders: [],
          careers: [],
          firmContact: defaultFirmContact,
        },
      };
    }
  },
  head: ({ matches, loaderData }) => {
    const pathname = matches.at(-1)?.pathname ?? "/";
    const seo = buildPublicIndexHead({ siteUrl: loaderData?.siteUrl, pathname });

    return {
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: DEFAULT_SITE_TITLE },
      { name: "description", content: DEFAULT_SITE_DESCRIPTION },
      { name: "author", content: "KS Lau & Co" },
      { name: "google-site-verification", content: "gt4iqEBtgtd6DIl033FsTiS-7jSuDTRORyqObXWDjzU" },
      ...buildOpenGraphMeta({
        siteUrl: loaderData?.siteUrl,
        pathname,
        title: DEFAULT_SITE_TITLE,
        description: DEFAULT_SITE_DESCRIPTION,
      }),
      ...buildTwitterMeta({
        siteUrl: loaderData?.siteUrl,
        title: DEFAULT_SITE_TITLE,
        description: DEFAULT_SITE_DESCRIPTION,
      }),
      ...seo.meta,
    ],
    links: [
      ...seo.links,
      {
        rel: "preload",
        href: "/fonts/manrope-latin.woff2",
        as: "font",
        type: "font/woff2",
        crossOrigin: "anonymous",
      },
      { rel: "stylesheet", href: appCss },
      // Stable, crawlable URLs — Googlebot cannot use Vite-inlined data: URIs.
      { rel: "icon", type: "image/png", sizes: "192x192", href: "/favicon-192.png" },
      { rel: "icon", type: "image/png", sizes: "32x32", href: "/favicon-32.png" },
      { rel: "icon", type: "image/png", sizes: "512x512", href: "/favicon-512.png" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/favicon-180.png" },
    ],
  };
  },
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  const settings = Route.useLoaderData();
  const gaId = settings?.googleAnalyticsId;

  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        {gaId ? <DeferredAnalytics gaId={gaId} /> : null}
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const settings = Route.useLoaderData();
  const location = useLocation();
  const isHome = location.pathname === "/";
  const isAdmin = location.pathname.startsWith("/admin");

  useTrackPageView();

  if (isAdmin) {
    return (
      <QueryClientProvider client={queryClient}>
        <Outlet />
        <Toaster richColors position="top-center" />
      </QueryClientProvider>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <SiteMotionProvider cardAnimationsEnabled={settings.cardAnimationsEnabled ?? true}>
        <SearchIndexProvider data={settings.searchData as SearchIndexInput}>
          <SiteHeader />
          <main className={isHome ? "min-h-screen" : "min-h-screen pt-14 md:pt-16"}>
            <Outlet />
          </main>
          <SiteFooter />
          <FloatingActions />
          <StickyHeroSearch />
          <Toaster richColors position="top-center" />
        </SearchIndexProvider>
      </SiteMotionProvider>
    </QueryClientProvider>
  );
}
